/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react';
import { arrayOf, bool, number, object, shape, string } from 'prop-types';
import { useSelector } from 'react-redux';
import Icon from 'yoda-core-components/lib/components/Icon';
import Loader from 'yoda-core-components/lib/components/Loader';
import Warning from 'yoda-core-components/lib/assets/svg/Warning.svg';
import classNames from 'classnames/bind';
import styles from './FilterCategory.css';
import Facet from '../Facet/Facet';
import FacetLabel from '../Facet/components/FacetLabel/FacetLabel';
import Search from '../StaticSearch/StaticSearch';
import PillWrapperConnected from '../PillWrapper/PillWrapper';
import CustomPriceRangeConnected from './components/CustomPriceRange';
import { getRandomId } from '../../utils';
import { filterCategoryPlaceHolder } from '../../configurations/config';

const cx = classNames.bind(styles);

const compareKeys = (key, searchKey) => {
    const regex = new RegExp(searchKey, 'i');
    const keysMatch = regex.test(key);

    return !!keysMatch;
};

const FilterCategory = ({
    dataSource: config,
    dataSource: { facetId, facetName, facetType, facetValueList, searchAvailable },
    pillFormat,
    placeHolder,
}) => {
    const {
        context: {
            deviceType: { isDesktop = true } = {},
            featureFlags: { enableMultiSelectFilters = false } = {},
        } = {},
        enableLoader = false,
        multiSelectFilter: { enableFacetLoader = false } = {},
    } = useSelector((store) => store);

    const [facets, setFacets] = useState(null);
    const hasSearch = isDesktop && searchAvailable;

    const getFacets = useCallback(
        (facetConfigs, fromFilterSearch = false) => {
            if (pillFormat)
                setFacets(<PillWrapperConnected config={config} placeHolder={placeHolder} />);

            if (fromFilterSearch && facetConfigs.length === 0) {
                setFacets(
                    <div className="flex mx-0 my-1 normal-case text-small">
                        <Icon className="flex-none mr-half mt-half" height="24" width="24">
                            <Warning />
                        </Icon>
                        No results found. Please try a different keyword.
                    </div>
                );

                return;
            }
            switch (facetType) {
                case 'prange':
                    setFacets(
                        <>
                            {facetConfigs.map((facetConfig) => (
                                <Facet
                                    config={config}
                                    facetConfig={facetConfig}
                                    key={getRandomId()}
                                    placeHolder={placeHolder}
                                />
                            ))}
                            <CustomPriceRangeConnected facetId={facetId} />
                        </>
                    );
                    break;

                case 'color':
                    setFacets(
                        <div className="flex flex-wrap justify-center my-1">
                            {facetConfigs.map((facetConfig) => (
                                <FacetLabel
                                    config={config}
                                    facetConfig={facetConfig}
                                    facetType={facetType}
                                    key={getRandomId()}
                                    large={isDesktop}
                                    theme="default"
                                />
                            ))}
                        </div>
                    );
                    break;

                case 'externalLink':
                    setFacets(
                        <div className={cx('clear-fix external-links')}>
                            {facetConfigs.map((facetConfig) => (
                                <div>
                                    <a
                                        className={[
                                            'inline-block',
                                            'mx-0 my-1',
                                            'normal-case',
                                            'text-small',
                                            styles.facetLink,
                                        ].join(' ')}
                                        href={facetConfig.apiUrl}
                                    >
                                        {facetConfig.name}
                                    </a>
                                </div>
                            ))}
                        </div>
                    );
                    break;

                default:
                    setFacets(
                        <div>
                            {facetConfigs.map((facetConfig) => (
                                <Facet
                                    config={config}
                                    facetConfig={facetConfig}
                                    key={getRandomId()}
                                    placeHolder={placeHolder}
                                />
                            ))}
                        </div>
                    );
                    break;
            }
        },
        [config, facetId, facetType, isDesktop, pillFormat, placeHolder]
    );

    useEffect(() => {
        let searchValue = document.getElementById(`static-search-${facetName}`);
        searchValue = searchValue && searchValue.value;
        if (searchValue) {
            getFacets(
                facetValueList.filter((facetConfig) =>
                    compareKeys(
                        facetConfig.name.replace(/[^a-zA-Z0-9 ]/g, ''),
                        searchValue.replace(/[^a-zA-Z0-9 ]/g, '')
                    )
                ),
                true
            );
        } else getFacets(facetValueList);
    }, [facetValueList, getFacets]);

    const filterCategoryConfig = {
        search: {
            onSearchChange: (searchValue) => {
                getFacets(
                    facetValueList.filter((facetConfig) =>
                        compareKeys(
                            facetConfig.name.replace(/[^a-zA-Z0-9 ]/g, ''),
                            searchValue.replace(/[^a-zA-Z0-9 ]/g, '')
                        )
                    ),
                    true
                );
            },
            id: `static-search-${facetName}`,
            placeHolder: `Search ${facetName}`,
        },
        wrapper: {
            className: cx('px-2', {
                'no-search': !hasSearch && isDesktop,
                'overflow-y-auto': isDesktop,
                search: hasSearch && isDesktop,
            }),
        },
    };

    return (
        <div {...filterCategoryConfig.wrapper}>
            {enableFacetLoader && enableLoader && enableMultiSelectFilters && (
                <Loader className="bg-gray-100 pt-5 z-10" style={{ alignItems: 'flex-start' }} />
            )}
            {searchAvailable && !pillFormat && <Search {...filterCategoryConfig.search} />}
            {facets}
        </div>
    );
};

FilterCategory.propTypes = {
    dataSource: shape({
        facetValueList: arrayOf(object).isRequired,
        facetId: number,
        facetName: string,
        facetType: string,
        searchAvailable: bool,
    }).isRequired,
    pillFormat: bool,
    placeHolder: string,
};

FilterCategory.defaultProps = {
    pillFormat: false,
    placeHolder: filterCategoryPlaceHolder.filterPanel,
};

export default FilterCategory;
